// Call Service for Twilio Integration - Simplified Call-Wide Listening Only
import api from "./api";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";
import { handleApiCall } from "../utils/apiResponseHandler";
import { showToast } from "../utils/show-toast";
import { transformErrorResponse } from "../utils/transform-error-response";
import { removeRealtimeChannel } from "./realtimeTeardown";

// Call initiation request
export interface InitiateCallRequest {
  to: string;
  from?: string; // Optional: backend will auto-select best caller ID
  lead_id?: string;
  recording_enabled?: boolean;
  dtmf_qualification?: boolean;
  metadata?: Record<string, any>;
}

// Call initiation response
export interface InitiateCallResponse {
  call_id: string;
  status: 'queued' | 'initiated' | 'ringing' | 'answered' | 'completed' | 'failed';
  to: string;
  from: string;
  created_at: string;
  twilio_sid?: string;
}

// Call status response
export interface CallStatusResponse {
  call_id: string;
  status: string;
  duration?: number;
  recording_url?: string;
  ended_at?: string;
  outcome?: string;
}

// Multi-line call request
export interface MultiLineCallRequest {
  leads: Array<{
    lead_id: string;
    phone: string;
    name?: string;
  }>;
  max_concurrent?: number;
  recording_enabled?: boolean;
  aiMatchingEnabled?: boolean;
}

// Multi-line call response
export interface MultiLineCallResponse {
  sessionId: string;
  dialMode: string;
  fromNumber: string;
  results: Array<{
    to: string;
    lead_id?: string;
    lead_name?: string;
    from: string;
    callSid: string | null;
    call_id: string | null;
    status: string;
    success: boolean;
    error?: string;
  }>;
  summary: {
    total: number;
    successful: number;
    failed: number;
  };
  initiatedAt: string;
}

// Race-to-answer request - calls multiple leads simultaneously, first to answer wins
export interface RaceToAnswerRequest {
  phoneNumbers: Array<{
    phone: string;
    leadId?: string;
    name?: string;
  }>;
  fromPhoneNumber: string;
  metadata?: Record<string, any>;
  // Full lead queue for batch progression (e.g., 50 leads)
  // Only the first batch (phoneNumbers) is dialed immediately
  // Remaining leads are stored for automatic progression when batch completes
  fullLeadQueue?: Array<{
    phone: string;
    leadId?: string;
    name?: string;
    fromNumber?: string;
  }>;
}

// Race-to-answer response
export interface RaceToAnswerResponse {
  sessionId: string;
  callsInitiated: number;
  callSids: string[];
  conferenceName: string;
  fromPhoneNumber?: string;
  // How many selected leads were skipped because they're outside TCPA calling
  // hours (8am–9pm their local time). Surfaced to the agent as a toast.
  skippedOutsideHours?: number;
}

// Multi-line session status
export interface MultiLineSessionLine {
  lineId: string;
  lineNumber: number;
  customerPhone: string;
  customerName?: string;
  leadId?: string;
  customerCallSid?: string; // Twilio call SID (starts with "CA")
  status: 'idle' | 'dialing' | 'ringing' | 'connected' | 'on-hold' | 'disconnected';
  connectedAt?: string;
  endedAt?: string;
  duration?: number;
  // AMD (Answering Machine Detection) fields
  amdStatus?: string; // 'human' | 'unknown' | 'machine_start' | 'machine_end_beep' | etc.
  amdDetectedAt?: string;
  isVoicemail?: boolean;
  voicemailDropped?: boolean;
  // Per-line AI-matched caller ID
  from?: string;
}

export interface MultiLineSessionStatus {
  sessionId: string;
  userId: string;
  status: 'active' | 'completed' | 'cancelled';
  lines: MultiLineSessionLine[];
  maxLines: number;
  mode: 'standard' | 'race-to-answer';
  createdAt: string;
  updatedAt: string;
}

// 3-Way Calling Types
export type ParticipantRole = 'agent' | 'customer' | 'consultant';
export type ParticipantStatus = 'ringing' | 'connected' | 'hold' | 'disconnected';

export interface ConferenceParticipant {
  participantSid: string;
  callSid: string;
  phoneNumber: string;
  name?: string;
  role: ParticipantRole;
  status: ParticipantStatus;
  isMuted: boolean;
  isOnHold: boolean;
  joinedAt: string;
}

export interface AddParticipantRequest {
  conferenceName: string;
  phoneNumber: string;
  name?: string;
  role?: ParticipantRole;
}

export interface AddParticipantResponse {
  success: boolean;
  participant?: ConferenceParticipant;
  error?: string;
}

export interface ConferenceParticipantsResponse {
  success: boolean;
  participants: ConferenceParticipant[];
  conferenceSid?: string;
  error?: string;
}

export interface ControlParticipantRequest {
  conferenceName: string;
  participantSid: string;
  action: 'mute' | 'unmute' | 'hold' | 'unhold' | 'remove';
}

export interface ControlParticipantResponse {
  success: boolean;
  participant?: ConferenceParticipant;
  error?: string;
}

// Call-wide listening request
export interface CallWideListeningRequest {
  leadId: string;
  leadName: string;
  listeningMessage?: string;
  callDuration?: number; // in seconds
}

// Call-wide listening response
export interface CallWideListeningResponse {
  success: boolean;
  message: string;
  twiML: string;
  listeningDuration: number;
}

// Call-wide processing request
export interface CallWideProcessingRequest {
  callSid: string;
  from: string;
  to: string;
  digits: string;
  callStatus: string;
  direction: string;
}

// Call-wide processing response
export interface CallWideProcessingResponse {
  success: boolean;
  action: string;
  message: string;
  leadStatus?: string;
  agentNumber?: string;
  capturedData?: {
    ssn?: string;
    driversLicense?: string;
    dob?: string;
  };
}

class CallService {
  /**
   * Initiate a call to a lead
   */
  async initiateCall(request: InitiateCallRequest): Promise<InitiateCallResponse> {
    try {
      const result = await handleApiCall<InitiateCallResponse>(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.communication.initiateCall),
          body: request,
          auth: true,
        });
        return response;
      });

      return result;
    } catch (error) {

      showToast(transformErrorResponse(error)?.message || "Failed to initiate call", "error");
      throw error;
    }
  }

  /**
   * Initiate multi-line calls
   */
  async initiateMultiLineCall(request: MultiLineCallRequest): Promise<MultiLineCallResponse> {
    try {
      const result = await handleApiCall<MultiLineCallResponse>(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.communication.initiateMultiLineCall),
          body: request,
          auth: true,
        });
        return response;
      });

      return result;
    } catch (error) {

      showToast(transformErrorResponse(error)?.message || "Failed to initiate multi-line calls", "error");
      throw error;
    }
  }

  /**
   * Initiate race-to-answer session
   * Calls multiple leads simultaneously - first to answer wins, others get canceled
   */
  async initiateRaceToAnswer(request: RaceToAnswerRequest): Promise<RaceToAnswerResponse> {
    try {
      const result = await handleApiCall<RaceToAnswerResponse>(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.communication.initiateRaceToAnswer),
          body: request,
          auth: true,
        });
        return response;
      });

      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message || "Failed to initiate race-to-answer calls", "error");
      throw error;
    }
  }

  /**
   * Get multi-line session status
   */
  async getMultiLineSessionStatus(sessionId: string): Promise<MultiLineSessionStatus> {
    try {
      const result = await handleApiCall<MultiLineSessionStatus>(async () => {
        const response = await api.get({
          url: getApiUrl(endpoint.communication.getMultiLineSession(sessionId)),
          auth: true,
        });
        return response;
      });

      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get call status
   */
  async getCallStatus(callId: string): Promise<CallStatusResponse> {
    try {
      const result = await handleApiCall<CallStatusResponse>(async () => {
        const response = await api.get({
          url: getApiUrl(endpoint.communication.getCallStatus(callId)),
          auth: true,
        });
        return response;
      });

      return result;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Get call history
   */
  async getCallHistory(limit: number = 50, offset: number = 0): Promise<any[]> {
    try {
      const result = await handleApiCall<any[]>(async () => {
        const response = await api.get({
          url: getApiUrl("/communication/call-history"),
          params: { limit, offset },
          auth: true,
        });
        return response;
      });

      return result;
    } catch (error) {

      throw error;
    }
  }

  // Note: Call-wide listening is now automatically enabled on every call
  // No manual start/stop methods needed - the backend handles this automatically

  /**
   * Process call-wide DTMF input
   */
  async processCallWideInput(request: CallWideProcessingRequest): Promise<CallWideProcessingResponse> {
    try {
      const result = await handleApiCall<CallWideProcessingResponse>(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.communication.dtmfProcessCallWide),
          body: request,
          auth: true,
        });
        return response;
      });

      return result;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Process typed call-wide DTMF input
   */
  async processCallWideTypedInput(request: CallWideProcessingRequest): Promise<CallWideProcessingResponse> {
    try {
      const result = await handleApiCall<CallWideProcessingResponse>(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.communication.dtmfProcessCallWideTyped),
          body: request,
          auth: true,
        });
        return response;
      });

      return result;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Add participant to existing call/conference (3-way calling)
   */
  async addParticipantToCall(request: AddParticipantRequest): Promise<AddParticipantResponse> {
    try {
      const result = await handleApiCall<AddParticipantResponse>(async () => {
        const response = await api.post({
          url: getApiUrl("/twilio/conference/add-participant"),
          body: request,
          auth: true,
        });
        return response;
      });

      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message || "Failed to add participant to call", "error");
      throw error;
    }
  }

  /**
   * Get all participants in a conference
   */
  async getConferenceParticipants(conferenceName: string): Promise<ConferenceParticipantsResponse> {
    try {
      const result = await handleApiCall<ConferenceParticipantsResponse>(async () => {
        const response = await api.get({
          url: getApiUrl(`/twilio/conference/${encodeURIComponent(conferenceName)}/participants`),
          auth: true,
        });
        return response;
      });

      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Control a participant (mute, hold, remove)
   */
  async controlParticipant(request: ControlParticipantRequest): Promise<ControlParticipantResponse> {
    try {
      const result = await handleApiCall<ControlParticipantResponse>(async () => {
        const response = await api.post({
          url: getApiUrl("/twilio/conference/control-participant"),
          body: request,
          auth: true,
        });
        return response;
      });

      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message || "Failed to control participant", "error");
      throw error;
    }
  }
}

// Call Status Tracker for real-time updates
// Uses Supabase Realtime broadcast as primary, HTTP polling as 30s fallback
export class CallStatusTracker {
  private static instance: CallStatusTracker;
  private callStatuses: Map<string, CallStatusResponse> = new Map();
  private listeners: Map<string, Array<(status: CallStatusResponse) => void>> = new Map();
  private pollingIntervals: Map<string, NodeJS.Timeout> = new Map();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private realtimeChannels: Map<string, any> = new Map();
  private onCallCompleteCallbacks: Array<(callId: string, status: CallStatusResponse) => void> = [];
  private userId: string | null = null;

  static getInstance(): CallStatusTracker {
    if (!CallStatusTracker.instance) {
      CallStatusTracker.instance = new CallStatusTracker();
    }
    return CallStatusTracker.instance;
  }

  /**
   * Set the current user ID for Supabase Realtime subscriptions.
   * Must be called before startStatusPolling for realtime to work.
   */
  setUserId(id: string | null) {
    this.userId = id;
  }

  /**
   * Register a callback to be called when any call completes or fails
   * Useful for invalidating React Query caches
   */
  onCallComplete(callback: (callId: string, status: CallStatusResponse) => void) {
    this.onCallCompleteCallbacks.push(callback);
  }

  /**
   * Remove a callback
   */
  offCallComplete(callback: (callId: string, status: CallStatusResponse) => void) {
    const index = this.onCallCompleteCallbacks.indexOf(callback);
    if (index > -1) {
      this.onCallCompleteCallbacks.splice(index, 1);
    }
  }

  subscribeToCallStatus(callId: string, callback: (status: CallStatusResponse) => void) {
    if (!this.listeners.has(callId)) {
      this.listeners.set(callId, []);
    }
    this.listeners.get(callId)!.push(callback);
  }

  updateCallStatus(callId: string, status: CallStatusResponse) {
    this.callStatuses.set(callId, status);
    const callListeners = this.listeners.get(callId);
    if (callListeners) {
      callListeners.forEach(callback => callback(status));
    }
  }

  getCallStatus(callId: string): CallStatusResponse | undefined {
    return this.callStatuses.get(callId);
  }

  private handleTerminalStatus(callId: string, status: CallStatusResponse) {
    this.stopStatusPolling(callId);
    this.onCallCompleteCallbacks.forEach(callback => {
      try {
        callback(callId, status);
      } catch (error) {
        // Ignore callback errors
      }
    });
  }

  startStatusPolling(callId: string, intervalMs: number = 30000) {
    if (this.pollingIntervals.has(callId)) {
      clearInterval(this.pollingIntervals.get(callId)!);
    }

    const terminationStatuses = ['completed', 'failed', 'busy', 'no-answer', 'canceled', 'rejected'];

    // Subscribe to Supabase Realtime broadcast for instant status updates
    // Uses dedicated :callstatus suffix to avoid channel collisions with
    // IncomingCallContext (base), MultiLineDialer (:multiline), DTMF (:dtmf)
    if (this.userId && !this.realtimeChannels.has(callId)) {
      try {
        const { getSupabaseClientSync } = require('./supabaseClient');
        const supabase = getSupabaseClientSync();
        if (supabase) {
          const channelName = `incoming_calls:${this.userId}:callstatus`;
          const channel = supabase.channel(channelName);

          channel.on('broadcast', { event: 'call_status_updated' }, (message: { payload?: Record<string, unknown> }) => {
            const payload = message?.payload;
            if (!payload || payload.callSid !== callId) return;

            const statusResponse: CallStatusResponse = {
              call_id: callId,
              status: payload.status as string,
              duration: payload.duration as number | undefined,
              recording_url: payload.recordingUrl as string | undefined,
            };
            this.updateCallStatus(callId, statusResponse);

            if (terminationStatuses.includes(statusResponse.status)) {
              this.handleTerminalStatus(callId, statusResponse);
            }
          });

          channel.subscribe();
          this.realtimeChannels.set(callId, channel);
        }
      } catch (error) {
        // Silently fail — HTTP polling fallback will handle it
      }
    }

    // HTTP polling fallback (30s default, was 5s)
    // Catches any missed realtime events
    const interval = setInterval(async () => {
      try {
        const svc = new CallService();
        const status = await svc.getCallStatus(callId);
        this.updateCallStatus(callId, status);

        if (terminationStatuses.includes(status.status)) {
          this.handleTerminalStatus(callId, status);
        }
      } catch (error) {
        // Silently fail
      }
    }, intervalMs);

    this.pollingIntervals.set(callId, interval);
  }

  stopStatusPolling(callId: string) {
    const interval = this.pollingIntervals.get(callId);
    if (interval) {
      clearInterval(interval);
      this.pollingIntervals.delete(callId);
    }

    // Unsubscribe from realtime channel
    const channel = this.realtimeChannels.get(callId);
    if (channel) {
      removeRealtimeChannel(channel);
      this.realtimeChannels.delete(callId);
    }
  }

  unsubscribeFromCallStatus(callId: string, callback: (status: CallStatusResponse) => void) {
    const callListeners = this.listeners.get(callId);
    if (callListeners) {
      const index = callListeners.indexOf(callback);
      if (index > -1) {
        callListeners.splice(index, 1);
      }
    }
  }
}

export const callService = new CallService();
export const callStatusTracker = CallStatusTracker.getInstance();
export default callService;